import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import DesktopAccessDisabledIcon from '@mui/icons-material/DesktopAccessDisabled'
import { ScreenGroupStats } from 'src/admin/core/services/screens/screenDashboardService'
import { useScreenDashboardContext } from '../ScreenDashboardContext'

interface ScreenGroupCardsProps {
  groups: ScreenGroupStats[]
}

const ScreenGroupCards = ({ groups }: ScreenGroupCardsProps) => {
  const { selectedScreenGroupId, setSelectedScreenGroupId } = useScreenDashboardContext()
  const isEmpty = !groups || groups.length === 0

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <DesktopAccessDisabledIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            textTransform: 'uppercase',
            letterSpacing: 0.8,
            color: 'text.secondary',
          }}
        >
          FALLAS POR CONFIGURACIÓN DE SCREEN (OFFLINE)
        </Typography>
      </Box>

      {/* Cards Grid */}
      {isEmpty ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No hay pantallas registradas
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {groups.map((group) => {
            const { screen_id, screen_name, total, online, failing, percentage } = group
            const hasFailures = failing > 0
            const isSelected = selectedScreenGroupId === screen_id

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={screen_id}>
                <Paper
                  elevation={0}
                  onClick={() => setSelectedScreenGroupId(isSelected ? null : screen_id)}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: isSelected ? '2px solid' : '1px solid',
                    borderColor: isSelected
                      ? (hasFailures ? '#ef4444' : 'primary.main')
                      : (hasFailures ? '#fca5a5' : 'divider'),
                    bgcolor: isSelected
                      ? (hasFailures ? 'rgba(239, 68, 68, 0.08)' : 'rgba(34, 197, 94, 0.08)')
                      : 'background.paper',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: 135,
                    cursor: 'pointer',
                    transform: isSelected ? 'translateY(-2px)' : 'none',
                    boxShadow: isSelected ? 4 : 0,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      borderColor: isSelected
                        ? (hasFailures ? '#ef4444' : 'primary.main')
                        : (hasFailures ? '#ef4444' : 'text.secondary'),
                    },
                  }}
                >
                  {/* Top Row: Dot + Screen Name + Circular Progress */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, mr: 1 }}>
                      {/* Status Dot */}
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: hasFailures ? '#ef4444' : '#22c55e',
                          flexShrink: 0,
                        }}
                      />
                      {/* Screen Name */}
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          fontSize: '0.68rem',
                          letterSpacing: 0.8,
                          color: 'text.secondary',
                        }}
                        title={`${screen_name}, FALLAN`}
                      >
                        {screen_name}, FALLAN
                      </Typography>
                    </Box>

                    {/* Circular Progress Ring */}
                    <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        sx={{ color: '#f1f5f9' }}
                        size={36}
                        thickness={4}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          color: percentage === 100 ? '#22c55e' : '#ef4444',
                          position: 'absolute',
                          left: 0,
                        }}
                        size={36}
                        thickness={4}
                      />
                      <Box
                        sx={{
                          top: 0,
                          left: 0,
                          bottom: 0,
                          right: 0,
                          position: 'absolute',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.62rem', fontWeight: 800, color: 'text.primary' }}
                        >
                          {percentage}%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Bottom Row: Large Failure Number + Online count */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '2.5rem',
                        lineHeight: 1,
                        color: hasFailures ? '#ef4444' : '#b2bec3', // Grey if 0, Red if > 0
                      }}
                    >
                      {failing}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.68rem',
                        color: 'text.secondary',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {online} de {total} online
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )
          })}
        </Grid>
      )}
    </Box>
  )
}

export default ScreenGroupCards
